import React, { useState } from 'react';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import { getImageUrl } from '../config';
import * as FiIcons from 'react-icons/fi';

const { FiLayers, FiArrowUp, FiCheckCircle, FiRefreshCw, FiAlertCircle } = FiIcons;

const HierarchyGame = () => {
  const initialItems = [
    { id: 'priest', name: 'Priest (Abatachin)', role: 'Religious Leader', level: 4, image: getImageUrl('priest.jpg') },
    { id: 'teacher', name: 'Teacher (Memhir)', role: 'Educator', level: 3, image: getImageUrl('male-teacher.jpg') },
    { id: 'parent', name: 'Parent (Ababa/Emama)', role: 'Family Head', level: 2, image: getImageUrl('father.jpg') },
    { id: 'friend', name: 'Friend (Gwadejna)', role: 'Peer', level: 1, image: getImageUrl('pointing-friend.jpg') }
  ];

  const [items, setItems] = useState([...initialItems].sort(() => Math.random() - 0.5));
  const [feedback, setFeedback] = useState(null);

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('dragIndex', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    const dragIndex = Number(e.dataTransfer.getData('dragIndex'));
    const newItems = [...items];
    const draggedItem = newItems[dragIndex];
    
    // Remove dragged item
    newItems.splice(dragIndex, 1);
    // Insert at new position
    newItems.splice(dropIndex, 0, draggedItem);
    
    setItems(newItems);
    setFeedback(null);
  };

  const checkOrder = () => {
    // Correct order is Level 4 (Top) to Level 1 (Bottom)
    const isCorrect = items.every((item, index) => {
      // Index 0 should be Level 4, Index 3 should be Level 1
      const expectedLevel = 4 - index;
      return item.level === expectedLevel;
    });

    if (isCorrect) {
      setFeedback({ type: 'success', message: 'Perfect! You understood the social hierarchy correctly.' });
    } else {
      setFeedback({ type: 'error', message: 'Not quite. Remember: Religious leaders are highest, followed by teachers/elders, then parents, then friends.' });
    }
  };

  const resetGame = () => {
    setItems([...initialItems].sort(() => Math.random() - 0.5));
    setFeedback(null);
  };

  return (
    <PageLayout
      title="Community Hierarchy"
      subtitle="Arrange the community members from Highest Respect (Top) to Casual/Equal (Bottom)."
      icon={FiLayers}
      color="text-purple-600"
      bgColor="bg-purple-50"
      prevLink="/teacher-hub"
      prevLabel="Teacher Hub"
    >
      <div className="max-w-3xl mx-auto fade-in">
        <div className="flex flex-col md:flex-row gap-8">
          
          {/* Instructions */}
          <div className="md:w-1/3 space-y-4">
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-2">How to Play</h3>
              <p className="text-sm text-gray-600 mb-4">
                Drag and drop the cards to arrange them. Who gets the most formal respect?
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-purple-700 font-bold">
                  <SafeIcon icon={FiArrowUp} /> Highest Respect (Top)
                </div>
                <div className="h-8 border-l-2 border-dashed border-gray-300 ml-3"></div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="w-4 h-4 rounded-full border-2 border-gray-400"></span> Casual / Equal (Bottom)
                </div>
              </div>
            </div>

            <button 
              onClick={checkOrder}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              Check Answer
            </button>
             <button 
              onClick={resetGame}
              className="w-full py-3 bg-white text-gray-600 border border-gray-200 rounded-xl font-bold hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiRefreshCw} /> Reset
            </button>
          </div>

          {/* Game Area - Ladder */}
          <div className="flex-1 relative">
            {/* Ladder Visual Background */}
            <div className="absolute inset-x-8 top-0 bottom-0 border-x-4 border-gray-200 rounded-lg -z-10"></div>
            
            <div className="space-y-4">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm hover:border-purple-400 hover:shadow-md cursor-grab active:cursor-grabbing transition-all flex items-center gap-4 relative z-10"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full text-gray-400 font-bold">
                    {index + 1}
                  </div>
                  <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover bg-gray-100" />
                  <div>
                    <h4 className="font-bold text-gray-800">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.role}</p>
                  </div>
                  <div className="ml-auto text-gray-300">
                    <SafeIcon icon={FiLayers} />
                  </div>
                </div>
              ))}
            </div>

            {/* Feedback Overlay */}
            {feedback && (
              <div className={`mt-6 p-4 rounded-xl border-2 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2 ${
                feedback.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <SafeIcon icon={feedback.type === 'success' ? FiCheckCircle : FiAlertCircle} className="text-xl mt-0.5" />
                <div>
                  <h4 className="font-bold">{feedback.type === 'success' ? 'Correct!' : 'Try Again'}</h4>
                  <p className="text-sm opacity-90">{feedback.message}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default HierarchyGame;